import { useCallback } from 'react';
import type { SeverityType } from '@kirin/api-client/contracts/shared/severity';
import {
  useAccountEnforcementSeverityThreshold,
  useUpsertAccountEnforcementSeverityThreshold,
} from '@/hooks/api/useAccountSettings';
import { useUserPolicies } from '@/hooks/api/useUserPolicies';
import { useToast } from '@/hooks/use-toast';
import { hasAnyEnforcePolicy as anyPolicyEnforces } from '../hasAnyEnforcePolicy';

type Result = {
  severity: SeverityType;
  isLoading: boolean;
  hasError: boolean;
  isSaving: boolean;
  hasAnyEnforcePolicy: boolean;
  onChange: (next: SeverityType) => Promise<void>;
};

export function usePersonalEnforcementSeverity(enabled: boolean): Result {
  const { toast } = useToast();
  const enforcementQuery = useAccountEnforcementSeverityThreshold(enabled);
  const mutation = useUpsertAccountEnforcementSeverityThreshold();
  const { data: policies } = useUserPolicies();

  const hasAnyEnforcePolicy = anyPolicyEnforces(policies);

  const onChange = useCallback(
    async (severity: SeverityType): Promise<void> => {
      try {
        await mutation.mutateAsync({ severity });
        toast({
          title: 'Settings saved',
          description: 'Enforcement severity threshold updated successfully',
        });
      } catch {
        toast({
          variant: 'destructive',
          title: 'Failed to update',
          description: 'Could not save enforcement severity threshold',
        });
      }
    },
    [mutation, toast]
  );

  return {
    severity: enforcementQuery.severity ?? 'high',
    isLoading: enforcementQuery.isPending,
    hasError: enforcementQuery.isError,
    isSaving: mutation.isPending,
    hasAnyEnforcePolicy,
    onChange,
  };
}
